import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { formatDate } from "@/lib/formatters";
import { StatusBadge } from "@/components/StatusBadge";
import { Pagination } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateInput } from "@/components/DateInput";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Plus, Search, Package, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 20;

export default function Inventories() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const canCreate = hasPermission("inventories.create");
  const canEditAll = hasPermission("inventories.edit_all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [skip, setSkip] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.id)));
    }
  };

  const handleExport = async (mode, kind = "pdf") => {
    try {
      setExporting(true);
      const body = mode === "all" ? { all: true } : { ids: Array.from(selected) };
      let path = "/export/inventories", mime = "application/pdf", ext = "pdf";
      if (kind !== "pdf") {
        path = "/export/inventories/sheet";
        body.format = kind;
        ext = kind;
        mime = kind === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      }
      const resp = await api.post(path, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `inventari_${mode === "all" ? "tutti" : selected.size}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Esportazione completata");
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Export failed:", err);
      toast.error("Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (canEditAll) {
      api.get("/customers?limit=500").then(r => setCustomers(r.data.items || [])).catch(() => {});
    }
  }, [canEditAll]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (customerFilter !== "all") params.set("customer_id", customerFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      params.set("skip", skip);
      params.set("limit", PAGE_SIZE);
      const { data } = await api.get(`/inventories?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error("Errore nel caricamento inventari");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, customerFilter, dateFrom, dateTo, skip]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setSkip(0); }, [search, statusFilter, customerFilter, dateFrom, dateTo]);

  return (
    <div data-testid="inventories-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tighter font-['Outfit']">Inventari</h1>
          <p className="text-sm text-zinc-500">{total} inventari totali</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-sm" disabled={exporting} data-testid="export-inventories-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                  Esporta
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>PDF</DropdownMenuLabel>
                <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "pdf")} data-testid="export-selected-inventories">
                  Selezionati ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("all", "pdf")} data-testid="export-all-inventories">
                  Tutti
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Excel (contabilità)</DropdownMenuLabel>
                <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "xlsx")} data-testid="export-selected-inventories-xlsx">
                  Selezionati ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("all", "xlsx")} data-testid="export-all-inventories-xlsx">
                  Tutti
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>CSV</DropdownMenuLabel>
                <DropdownMenuItem disabled={selected.size === 0} onClick={() => handleExport("selected", "csv")} data-testid="export-selected-inventories-csv">
                  Selezionati ({selected.size})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("all", "csv")} data-testid="export-all-inventories-csv">
                  Tutti
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button data-testid="new-inventory-btn" onClick={() => navigate("/inventories/new")}
              className="bg-[#002FA7] hover:bg-[#002482] rounded-sm">
              <Plus className="w-4 h-4 mr-1" /> Nuovo Inventario
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input data-testid="inventory-search" placeholder="Cerca per numero..." value={search}
            onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-sm" />
        </div>
        {canEditAll && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="inventory-status-filter" className="w-full sm:w-[150px] rounded-sm">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="DRAFT">Bozza</SelectItem>
              <SelectItem value="ISSUED">Emesso</SelectItem>
              <SelectItem value="SUPERSEDED">Sostituito</SelectItem>
              <SelectItem value="CANCELLED">Annullato</SelectItem>
              <SelectItem value="ARCHIVED">Archiviato</SelectItem>
            </SelectContent>
          </Select>
        )}
        {canEditAll && customers.length > 0 && (
          <Select value={customerFilter} onValueChange={setCustomerFilter}>
            <SelectTrigger data-testid="inventory-customer-filter" className="w-full sm:w-[180px] rounded-sm">
              <SelectValue placeholder="Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i clienti</SelectItem>
              {customers.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="flex gap-2 w-full sm:w-auto">
          <DateInput data-testid="inventory-date-from" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-sm flex-1 sm:w-[150px]" />
          <DateInput data-testid="inventory-date-to" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-sm flex-1 sm:w-[150px]" />
        </div>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      <div className="card-surface overflow-hidden">
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="w-10 px-3 py-3">
                  <Checkbox checked={items.length > 0 && selected.size === items.length}
                    onCheckedChange={toggleAll} data-testid="select-all-inventories" />
                </th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Numero</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Cliente</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden sm:table-cell">Data</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500 hidden md:table-cell">Viaggio</th>
                <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Stato</th>
                <th className="text-right px-5 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">Elementi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id}
                  className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer transition-colors"
                  data-testid={`inventory-row-${inv.id}`}>
                  <td className="w-10 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.has(inv.id)}
                      onCheckedChange={() => toggleSelect(inv.id)} data-testid={`select-inventory-${inv.id}`} />
                  </td>
                  <td className="px-5 py-3" onClick={() => navigate(`/inventories/${inv.id}`)}>
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm font-mono">{inv.number || "BOZZA"}</span>
                      {inv.inventory_type === "release" && (
                        <span className="inline-flex px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell" onClick={() => navigate(`/inventories/${inv.id}`)}>{inv.customer_name || "-"}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden sm:table-cell" onClick={() => navigate(`/inventories/${inv.id}`)}>{formatDate(inv.date)}</td>
                  <td className="px-5 py-3 text-sm text-zinc-600 hidden md:table-cell" onClick={() => navigate(`/inventories/${inv.id}`)}>N. {inv.trip_number || 1}</td>
                  <td className="px-5 py-3" onClick={() => navigate(`/inventories/${inv.id}`)}><StatusBadge status={inv.status} /></td>
                  <td className="px-5 py-3 text-sm text-right" onClick={() => navigate(`/inventories/${inv.id}`)}>{inv.items?.length || 0}</td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-8 text-center text-sm text-zinc-400">Nessun inventario trovato</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Mobile cards */}
        <div className="sm:hidden divide-y divide-zinc-100">
          {items.map((inv) => (
            <div key={inv.id} className="p-4 active:bg-zinc-50 transition-colors" data-testid={`inventory-card-${inv.id}`}>
              <div className="flex items-start gap-3">
                <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(inv.id)} onCheckedChange={() => toggleSelect(inv.id)} />
                </div>
                <div className="flex-1 min-w-0" onClick={() => navigate(`/inventories/${inv.id}`)}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-mono font-medium truncate">{inv.number || "BOZZA"}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-zinc-400">{inv.customer_name || ""}{inv.customer_name && inv.date ? " · " : ""}{formatDate(inv.date)}</span>
                    <span className="text-xs text-zinc-500">{inv.items?.length || 0} elementi</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-zinc-400">Nessun inventario trovato</div>
          )}
        </div>
        <Pagination skip={skip} limit={PAGE_SIZE} total={total} onPageChange={setSkip} />
      </div>
    </div>
  );
}
